import React from "react";
import clsx from "clsx";
import { useField } from "react-final-form";
import { RADIO_CLASS } from "./ConsentRadio";

export interface ConsentOptionCard {
  value: string;
  label: React.ReactNode;
  // A picture of the choice: a mini page, the banner buttons, ...
  preview?: React.ReactNode;
  // Small note on the trailing edge, e.g. "Default".
  note?: string;
}

const COLUMN_CLASSES = {
  1: "grid-cols-1",
  2: "grid-cols-2",
};

interface ConsentOptionCardsProps {
  field: string;
  options: ConsentOptionCard[];
  ariaLabel: string;
  columns?: 1 | 2;
  // "tile" stacks the preview above the label; "row" puts the radio, preview, and note on one line.
  layout?: "tile" | "row";
}

/** Radio choices drawn as cards, wired to a react-final-form field. */
export const ConsentOptionCards = ({
  field,
  options,
  ariaLabel,
  columns = 2,
  layout = "tile",
}: ConsentOptionCardsProps) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    className={clsx("grid w-full gap-[12px]", COLUMN_CLASSES[columns])}
  >
    {options.map((option) => (
      <OptionCard key={option.value} field={field} option={option} layout={layout} />
    ))}
  </div>
);

const OptionCard = ({
  field,
  option,
  layout,
}: {
  field: string;
  option: ConsentOptionCard;
  layout: "tile" | "row";
}) => {
  const { input } = useField(field, { type: "radio", value: option.value });
  const radio = <input {...input} type="radio" className={RADIO_CLASS} />;
  const note = option.note && (
    <span className="ml-auto shrink-0 text-[12px] font-medium leading-[16px] text-neutral-600">
      {option.note}
    </span>
  );

  return (
    <label
      className={clsx(
        "flex min-w-0 cursor-pointer rounded-[10px] border border-solid transition-colors",
        layout === "tile" ? "flex-col gap-[10px] p-[10px]" : "items-center gap-[12px] p-[12px]",
        input.checked
          ? "border-primary-600 bg-primary-50 ring-1 ring-inset ring-primary-600"
          : "border-neutral-300 bg-white hover:border-neutral-500",
      )}
    >
      {layout === "tile" ? (
        <>
          {option.preview}
          <span className="flex items-center gap-[8px] text-[14px] font-medium leading-[20px] text-neutral-900">
            {radio}
            {option.label}
            {note}
          </span>
        </>
      ) : (
        <>
          {radio}
          <span className="min-w-0 text-[14px] font-medium leading-[20px] text-neutral-900">
            {option.preview ?? option.label}
          </span>
          {note}
        </>
      )}
    </label>
  );
};
